import React, { useMemo, useState } from "react";
import "./BrokerageCalculator.css";

const DAYS = 252;
const PER_ORDER = 20;
const SLAB_FEE = 1000;
const SLAB_LAKH = 10;

const toNum = (v, fallback) => {
  const n = parseFloat(v);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

const inr = (n) => {
  const r = Math.round(n);
  if (r >= 1e7) return `₹${(r / 1e7).toFixed(2)} Cr`;
  if (r >= 1e5) return `₹${(r / 1e5).toFixed(2)} L`;
  return `₹${r.toLocaleString("en-IN")}`;
};

export default function BrokerageCalculator() {
  const [mode, setMode] = useState("order");
  const [capital, setCapital] = useState("10");
  const [orders, setOrders] = useState("20");
  const [rate, setRate] = useState("0.02");
  const [turnover, setTurnover] = useState("10");

  const r = useMemo(() => {
    const cap = toNum(capital, 10);
    const perDay =
      mode === "pct"
        ? (cap * 1e5 * toNum(turnover, 10) * toNum(rate, 0.02)) / 100
        : toNum(orders, 20) * PER_ORDER;
    const current = perDay * DAYS;
    const flatMonth = Math.ceil(cap / SLAB_LAKH) * SLAB_FEE;
    const flat = flatMonth * 12;
    const max = Math.max(current, flat, 1);
    return {
      cap,
      current,
      flat,
      flatMonth,
      save: Math.max(0, current - flat),
      curPct: (current / max) * 100,
      flatPct: (flat / max) * 100,
      breakEven: Math.ceil(flat / (PER_ORDER * DAYS)),
    };
  }, [mode, capital, orders, rate, turnover]);

  return (
    <form className="fb-calc" onSubmit={(e) => e.preventDefault()} aria-labelledby="fb-calc-h">
      <h2 id="fb-calc-h" className="fb-calc-title">Your brokerage, side by side</h2>
      <p className="fb-calc-sub">Enter your numbers. Results update as you type.</p>

      <fieldset className="mb-3">
        <legend className="fb-label">Your current pricing</legend>
        <div className="fb-seg" role="radiogroup">
          {[
            ["order", "₹20 per order"],
            ["pct", "% of turnover"],
          ].map(([val, text]) => (
            <label key={val} className={mode === val ? "is-on" : ""}>
              <input
                type="radio"
                name="fb-mode"
                value={val}
                checked={mode === val}
                onChange={() => setMode(val)}
              />
              <span>{text}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="row g-3 mb-3">
        <div className="col-sm-6">
          <label htmlFor="fb-cap" className="fb-label">Capital in F&amp;O (₹ lakh)</label>
          <input id="fb-cap" className="form-control fb-input" type="number" inputMode="numeric"
            min="10" step="10" value={capital} onChange={(e) => setCapital(e.target.value)} />
        </div>
        {mode === "order" ? (
          <div className="col-sm-6">
            <label htmlFor="fb-ord" className="fb-label">Executed orders per day</label>
            <input id="fb-ord" className="form-control fb-input" type="number" inputMode="numeric"
              min="1" step="5" value={orders} onChange={(e) => setOrders(e.target.value)} />
          </div>
        ) : (
          <>
            <div className="col-sm-6">
              <label htmlFor="fb-rate" className="fb-label">Brokerage rate (%)</label>
              <input id="fb-rate" className="form-control fb-input" type="number" inputMode="decimal"
                min="0.001" step="0.005" value={rate} onChange={(e) => setRate(e.target.value)} />
            </div>
            <div className="col-sm-6">
              <label htmlFor="fb-lev" className="fb-label">Daily turnover (× capital)</label>
              <input id="fb-lev" className="form-control fb-input" type="number" inputMode="numeric"
                min="1" step="1" value={turnover} onChange={(e) => setTurnover(e.target.value)} />
            </div>
          </>
        )}
      </div>

      <div className="fb-out" aria-live="polite">
        <div className="fb-bar-row">
          <div className="fb-bar-head"><span>Current brokerage, per year</span><strong>{inr(r.current)}</strong></div>
          <div className="fb-track"><div className="fb-fill fb-fill-loss" style={{ width: `${r.curPct}%` }} /></div>
        </div>
        <div className="fb-bar-row">
          <div className="fb-bar-head"><span>Asiatic fixed plan, per year</span><strong>{inr(r.flat)}</strong></div>
          <div className="fb-track"><div className="fb-fill fb-fill-keep" style={{ width: `${r.flatPct}%` }} /></div>
        </div>
        <div className="fb-keep">
          <span>You keep every year</span>
          <strong>{inr(r.save)}</strong>
        </div>
        <p className="fb-note">
          {mode === "order"
            ? `At ₹${r.cap} lakh, the fixed plan beats ₹20 per order above ${r.breakEven} orders a day.`
            : `Fixed fee on ₹${r.cap} lakh: ${inr(r.flatMonth)} a month, regardless of turnover.`}
        </p>
        <p className="fb-note">
          Brokerage only, 252 trading days. STT, exchange, SEBI and stamp charges are identical at every broker and excluded.
        </p>
      </div>
    </form>
  );
}
